import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { DISCIPLINE_NODES } from "@/lib/content";

// Accessible discipline constellation: centre "Your challenge" with selectable
// nodes. No physics gimmicks — a clean radial button layout.
export function DisciplineConstellation() {
  const [active, setActive] = useState(DISCIPLINE_NODES[0]);
  const n = DISCIPLINE_NODES.length;
  return (
    <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
      <div className="relative mx-auto aspect-square w-full max-w-[440px]" data-testid="discipline-constellation">
        <div className="absolute left-1/2 top-1/2 z-10 flex h-28 w-28 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border border-[var(--cobalt)] bg-[var(--paper)] text-center shadow-sm">
          <span className="font-mono text-[0.6rem] uppercase tracking-wider text-[var(--mineral)]">Centre</span>
          <span className="px-2 font-display text-sm font-bold text-navy">Your challenge</span>
        </div>
        {DISCIPLINE_NODES.map((node, idx) => {
          const angle = (idx / n) * 2 * Math.PI - Math.PI / 2;
          const r = 42;
          const x = 50 + r * Math.cos(angle);
          const y = 50 + r * Math.sin(angle);
          const isActive = active.key === node.key;
          return (
            <button
              key={node.key}
              onClick={() => setActive(node)}
              data-testid={`constellation-node-${node.key}`}
              aria-pressed={isActive}
              className={`btn-transition absolute z-20 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border p-1 text-center text-[0.6rem] font-medium leading-tight ${
                isActive
                  ? "border-[var(--cobalt)] bg-[var(--cobalt)] text-white scale-110"
                  : "border-[var(--line)] bg-[var(--paper)] text-navy hover:border-[var(--cobalt)]"
              }`}
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              {node.label.split(" ").slice(0, 2).join(" ")}
            </button>
          );
        })}
        <svg className="absolute inset-0 h-full w-full" aria-hidden="true">
          {DISCIPLINE_NODES.map((node, idx) => {
            const angle = (idx / n) * 2 * Math.PI - Math.PI / 2;
            const r = 42;
            const x = 50 + r * Math.cos(angle);
            const y = 50 + r * Math.sin(angle);
            return (
              <line
                key={node.key}
                x1="50%"
                y1="50%"
                x2={`${x}%`}
                y2={`${y}%`}
                stroke={active.key === node.key ? "#2867B2" : "#D9E1E6"}
                strokeWidth="1"
              />
            );
          })}
        </svg>
      </div>

      <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-8" data-testid="constellation-detail">
        <p className="eyebrow mb-3">Selected discipline</p>
        <h3 className="font-display text-2xl font-bold text-navy">{active.label}</h3>
        <p className="mt-3 text-[var(--mineral)]">{active.blurb}</p>
        <Link
          to={`/capabilities?discipline=${encodeURIComponent(active.filter)}`}
          data-testid="constellation-explore-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[var(--cobalt)] link-underline"
        >
          Explore related capabilities <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}
